import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BackButton from '../../components/BackButton';
import { getArticleById, updateArticle, uploadMedia } from '../../services/blogApi';
import './CreateArticle.css';

export default function EditArticle() {
  const { articleId } = useParams();
  const { user } = useAuth();
  const isPremium = user?.roles?.includes('ROLE_PREMIUM');
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [published, setPublished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const loadArticle = async () => {
      try {
        const response = await getArticleById(articleId);
        const article = response.data;
        setTitle(article.title);
        setDescription(article.description || '');
        setContent(article.content);
        setPublished(article.published);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    if (articleId) {
      loadArticle();
    }
  }, [articleId]);

  if (!isPremium) {
    return (
      <div className="create-article-container">
        <BackButton />
        <div className="premium-required">
          <h2>⭐ Premium Membership Required</h2>
          <p>You need a premium membership to edit articles.</p>
          <button onClick={() => navigate('/dashboard')} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="create-article-container">
        <BackButton />
        <div className="loading">Loading article...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="create-article-container">
        <BackButton />
        <div className="error">{error}</div>
      </div>
    );
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadMedia(articleId, file);
      const imageUrl = response.data.url;
      const imageTag = `<img src="${imageUrl}" alt="Uploaded image" style="max-width: 100%; height: auto; margin: 20px 0;" />`;
      setContent(prev => prev + '\n' + imageTag + '\n');
      alert('Image uploaded successfully!');
    } catch (err) {
      alert('Failed to upload image: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleVoiceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('audio/')) {
      alert('Please upload an audio file');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadMedia(articleId, file);
      const audioUrl = response.data.url;
      const audioTag = `<audio controls style="width: 100%; margin: 20px 0;">
        <source src="${audioUrl}" type="${response.data.contentType}">
        Your browser does not support the audio element.
      </audio>`;
      setContent(prev => prev + '\n' + audioTag + '\n');
      alert('Voice note uploaded successfully!');
    } catch (err) {
      alert('Failed to upload voice note: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async (publish = false) => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!content.trim()) {
      setError('Content is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await updateArticle(articleId, {
        title,
        description,
        content,
        published: publish,
      });

      if (publish) {
        alert('Article published successfully!');
        navigate(`/blog/${response.data.slug}`);
      } else {
        alert('Article saved successfully!');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="create-article-container">
      <BackButton />
      <div className="article-editor">
        <div className="editor-header">
          <div className="editor-actions">
            <button
              onClick={() => handleSave(false)}
              disabled={saving || uploading}
              className="save-button"
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving || uploading}
              className="publish-button"
            >
              {saving ? 'Publishing...' : published ? 'Update' : 'Publish'}
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="editor-content">
          <input
            type="text"
            className="article-title-input"
            placeholder="Article Title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <input
            type="text"
            className="article-description-input"
            placeholder="Short description (for SEO and preview)..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="media-upload-section">
            <label className="upload-button">
              {uploading ? 'Uploading...' : '📷 Upload Image'}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </label>
            <label className="upload-button">
              {uploading ? 'Uploading...' : '🎤 Upload Voice Note'}
              <input
                type="file"
                accept="audio/*"
                onChange={handleVoiceUpload}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          <textarea
            className="article-content-textarea"
            placeholder="Start writing your article here... You can use HTML for formatting."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={20}
          />

          <div className="editor-help">
            <p><strong>Tips:</strong></p>
            <ul>
              <li>Use HTML tags for formatting (e.g., &lt;p&gt;, &lt;h2&gt;, &lt;strong&gt;, &lt;em&gt;)</li>
              <li>Upload images or voice notes and they will be automatically inserted</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

